import { FC, useEffect, useState } from 'react'

interface LoadingProgressProps {
  isLoading: boolean
  type: 'situation' | 'outcome' | 'image' | 'recap'
}

const LOADING_STEPS = {
  situation: [
    { text: 'Analyzing market conditions...', duration: 1500 },
    { text: 'Generating characters...', duration: 2000 },
    { text: 'Crafting the scene...', duration: 2500 },
    { text: 'Adding dramatic tension...', duration: 2000 },
    { text: 'Almost ready...', duration: 3000 },
  ],
  outcome: [
    { text: 'Evaluating your decision...', duration: 1000 },
    { text: 'Calculating consequences...', duration: 1500 },
    { text: 'Updating the narrative...', duration: 2000 },
    { text: 'Applying market effects...', duration: 1500 },
    { text: 'Finalizing outcome...', duration: 2000 },
  ],
  image: [
    { text: 'Composing the scene...', duration: 2000 },
    { text: 'Rendering pixels...', duration: 3000 },
    { text: 'Adding retro effects...', duration: 2000 },
    { text: 'Applying nostalgia filter...', duration: 2000 },
  ],
  recap: [
    { text: 'Analyzing your journey...', duration: 2000 },
    { text: 'Reviewing key decisions...', duration: 2000 },
    { text: 'Calculating final stats...', duration: 1500 },
    { text: 'Writing your epic tale...', duration: 3000 },
  ],
}

const DEGEN_TIPS = [
  'Remember: WAGMI means nothing if you NGMI first.',
  'The best time to buy was yesterday. The second best time is when you\'re already underwater.',
  'Diamond hands are just paper hands with commitment issues.',
  'NFA but definitely FA.',
  'Every dip is a buying opportunity... until it isn\'t.',
  'Zoom out... no, not that far.',
  'The real gains were the friends we liquidated along the way.',
  'Trust the process. Also, DYOR.',
]

export const LoadingProgress: FC<LoadingProgressProps> = ({ isLoading, type }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [tip, setTip] = useState('')

  const steps = LOADING_STEPS[type]

  useEffect(() => {
    if (!isLoading) {
      setCurrentStep(0)
      return
    }

    // Set random tip
    const randomTip = DEGEN_TIPS[Math.floor(Math.random() * DEGEN_TIPS.length)]
    setTip(randomTip || 'Stay focused, degen.')

    // Progress through steps
    let stepIndex = 0
    const advanceStep = () => {
      if (stepIndex < steps.length - 1) {
        stepIndex++
        setCurrentStep(stepIndex)
      }
    }

    const timers: NodeJS.Timeout[] = []
    let elapsed = 0

    steps.forEach((step, index) => {
      if (index > 0) {
        const timer = setTimeout(advanceStep, elapsed)
        timers.push(timer)
      }
      elapsed += step.duration
    })

    return () => {
      timers.forEach(clearTimeout)
    }
  }, [isLoading, type])

  if (!isLoading) return null

  const progressPercent = ((currentStep + 1) / steps.length) * 100

  return (
    <div className="p-4 space-y-3">
      {/* Current Step */}
      <div className="flex items-center gap-3">
        <div className="w-5 h-5 relative">
          <div className="absolute inset-0 border-2 border-game-accent rounded-full animate-spin border-t-transparent" />
        </div>
        <span className="font-retro text-sm text-game-accent animate-pulse">
          {steps[currentStep]?.text || 'Loading...'}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-game-bg border border-game-border overflow-hidden">
        <div
          className="h-full bg-game-accent transition-all duration-500 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Step Indicators */}
      <div className="flex justify-between px-1">
        {steps.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-colors ${
              index <= currentStep ? 'bg-game-accent' : 'bg-game-border'
            }`}
          />
        ))}
      </div>

      {/* Degen Tip */}
      <div className="pt-2 border-t border-game-border/50">
        <p className="font-retro text-xs text-game-muted italic text-center">
          💡 {tip}
        </p>
      </div>
    </div>
  )
}
